import React, {useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {BottomSheetPanel} from "../../../components/CustomModel/BottomSheetPanel";
import {t} from "i18next";
import useAxios from "../../../utils/useAxios";
import {formatLastName, getGuestTokenConfig, buildProfileAction} from "../../../constants";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { MatchScoreColumns, SetSwitcher } from "../matches/MatchScoreColumns";
import {MatchStatusSelect} from "../matches/MatchStatusSelect";
import {MatchDetailsModal} from "../matches/MatchDetailsModal";
import {useMatchSync, getNormalizedSets} from "../../../hooks/useMatchSync";
import {getTeamName} from "../../../constants";
import MatchParticipantAvatars from "../../../components/MatchParticipantAvatars";
import useTournamentPlayerImages from "../../../hooks/useTournamentPlayerImages";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";

// p is a group participant entry (group.players[]/group.participants[]) -
// grade 1 carries {id, first_name, last_name} directly, grade 2 carries a
// {players: [{id, first_name, last_name}, {...}]} pair. Team matches
// (grade 3) never get avatars at all (a photo pair doesn't represent a team
// the way it does an individual/pair), so this returns null before even
// touching `images`.
const getAvatarData = (p, grade, images) => {
    if (!p || grade === 3) return null;
    const list = grade === 2 ? normalizeTeamPlayers(p.players, 2) : [p];
    const present = list.filter(pl => pl && pl.id);
    if (!present.length) return null;
    return {
        images: present.map(pl => images[pl.id]),
        names: present.map(pl => `${pl.first_name || ""} ${pl.last_name || ""}`.trim())
    };
};

const getName = (p, grade, index, groupCharCode, winner_side, row_side, onTeamClick, onPlayerClick) => {
    if (!p) return `${t("Participant")} ${groupCharCode}${index + 1}`;
    if (grade === 1) {
        const clickProps = (onPlayerClick && p.id) ? {
            onClick: (e) => { e.stopPropagation(); onPlayerClick(p.id); },
            style: { cursor: "pointer" }
        } : {};
        return (
            <div className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...clickProps}>
                {p?.first_name && <>{p.first_name[0]}. </>}<span className="uppercase0">{formatLastName(p.last_name)}</span>
            </div>
        );
    }
    if (grade === 2) {
        // Both halves of the pair, and only those - a pair whose roster
        // carries a third member (duplicate `sorting`, see
        // src/utils/normalizeTeamPlayers.js) printed three names on one side
        // of the match card.
        const pairPlayers = normalizeTeamPlayers(p.players, 2);
        return (
            <div>
                {pairPlayers.map((player, i) => {
                    const clickProps = (onPlayerClick && player.id) ? {
                        onClick: (e) => { e.stopPropagation(); onPlayerClick(player.id); },
                        style: { cursor: "pointer" }
                    } : {};
                    return (
                        <div key={i} className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...clickProps}>
                            {player.first_name && `${player.first_name}. `}<span className="uppercase0">{formatLastName(player.last_name)}</span>
                            {i < pairPlayers.length - 1 && <br/>}
                        </div>
                    );
                })}
            </div>
        );
    }
    const renderTeamName = (p, index) => {
        return <span className="black-name">
        {getTeamName(p, t, index + 1)}
        </span>
    };
    const teamClickProps = (onTeamClick && p?.name) ? {
        onClick: (e) => { e.stopPropagation(); onTeamClick(p.name); },
        style: { cursor: "pointer" }
    } : {};
    return <div className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...teamClickProps}>
        {renderTeamName(p, index)}
    </div>
};

const buildMatches = (group) => {
    const players = group.players?.length ? group.players : (group.participants || []);
    const existingMatches = [...(group.matches || [])].sort((a, b) => a.match_order - b.match_order);
    const pairs = [];
    let order = 1;
    let matchIndex = 0;

    for (let i = 0; i < players.length; i++) {
        for (let j = i + 1; j < players.length; j++) {
            const p1 = players[i] || null;
            const p2 = players[j] || null;
            pairs.push({ p1, p2, p1_index: i, p2_index: j, match: existingMatches[matchIndex] || null, match_order: order });
            order++;
            matchIndex++;
        }
    }
    return pairs;
};

// Whole match card highlighted together if either participant matches -
// same "test both, not just the one that happens to match" idea as
// Bracket.js/BracketClassic.js's own renderSide. Team (grade 3) participants
// only carry their own name here, not the roster - see
// fetchTeamRosterMap.js's own comment. Grade 1/2 participants only carry
// each player's first_name truncated to an initial - resolved via
// playerNames (see fetchPlayerNameMap.js's own comment) before matching.
const pairMatchesSearch = (p1, p2, group, tournamentGrade, searchChips, teamRoster, playerNames) => {
    const resolvePlayer = (p) => (p ? (playerNames?.get(p.id) || p) : p);
    const namesFor = (p) => {
        if (!p) return [];
        if (tournamentGrade === 1) return [resolvePlayer(p)];
        if (tournamentGrade === 2) return (p.players || []).map(resolvePlayer);
        return [p.name, ...(teamRoster?.get(p.name) || [])];
    };
    return chipsMatchEntity(searchChips, { names: [...namesFor(p1), ...namesFor(p2)], groupValue: group.number });
};

const TournamentGroupsMatchesModal = ({ isOpen, onClose, groups, setGroups, tournamentId, tournamentGrade, selectedMatchId, setSelectedMatchId, expandSignal, stages, activeStage, is_authorized, fetchGroups, searchChips = [], teamRoster, playerNames }) => {
    const axios = useAxios();
    const history = useHistory();
    const location = useLocation();
    const [scores, setScores] = useState({});
    const [loadingMatches, setLoadingMatches] = useState({});
    // Which set (0-4) each match's score inputs/SetSwitcher currently show -
    // keyed by matchKey like `scores` above, so the switcher (rendered in the
    // card's title row) and MatchScoreColumns (rendered lower in the same
    // card) stay in sync without either owning the state itself.
    const [activeSetIndexes, setActiveSetIndexes] = useState({});
    const [teamMenu, setTeamMenu] = useState(null);
    // { match, groupId, m } for whichever team match's "Details" was tapped -
    // same sub-matches drill-in Draws already has (TournamentGridMatchesModal.js).
    // groupId/m are carried alongside the match itself so this modal's own
    // saveMatch (which lazily creates the match row on first save) can still
    // be reused for MatchDetailsModal's onSave.
    const [detailsMatch, setDetailsMatch] = useState(null);
    const playerImages = useTournamentPlayerImages(axios, tournamentId, tournamentGrade !== 3);

    // Every group in this modal shares the same activeStage, so this is
    // computed once here (not per-group) for MatchDetailsModal below.
    const currentStage = stages?.find(s => s.id === activeStage);
    const isMultiSet = currentStage?.games_in_set === 6;

    // Same pattern as TournamentScheduleMatchesModal.js/TournamentGridMatchesModal.js's
    // own handlePlayerClick - replace (not push) so the tournament header's
    // back-arrow goBack() doesn't just undo this instead of leaving the tournament.
    const handlePlayerClick = (pId) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", pId);
        history.replace({ search: params.toString() });
    };

    // "Who's on this team" menu - mirrors Bracket.js/BracketClassic.js's own
    // openParticipantMenu, but reads the already-prefetched teamRoster map
    // (see fetchTeamRosterMap.js) instead of fetching on demand, since this
    // list already has it loaded for search-chip matching (pairMatchesSearch above).
    const openTeamMenu = (teamName) => {
        const roster = teamRoster?.get(teamName) || [];
        setTeamMenu({ title: teamName, actions: roster.map(p => buildProfileAction(p, history, location)) });
    };


    const syncMatchApi = useMatchSync(tournamentId, (res) => {
        if (res?.data?.updated_matches) {
            setLoadingMatches(prev => {
                const next = { ...prev };
                res.data.updated_matches.forEach(m => delete next[m.id]);
                return next;
            });
        }
    });

    const saveMatch = async (groupId, m, newFields = {}, setIndex = 0) => {
        let match = m.match;
        // ЗЕРКАЛИМ БЭКЕНД: Избавляемся от прыжков статуса!
        if (newFields.winner_side !== undefined && newFields.status === undefined) {
            const currentStatus = match?.status;
            // Ставим статус 7 только если матч еще не Retired (5) и не Walkover (6)
            if (currentStatus !== 5 && currentStatus !== 6 && currentStatus !== 7) {
                newFields.status = 7;
            }
        }
        if (!match) {
            const config = getGuestTokenConfig(tournamentId);
            try {
                const res = await axios.post(
                    `/api/v1.0/tournaments/${tournamentId}/matches/`,
                    { stage: groupId, match_order: m.match_order },
                    config
                );
                match = res.data;
                m.match = match; // Фикс гонки создания
            } catch (err) {
                console.error(err);
                return;
            }
        }

        if (newFields.status !== undefined) {
            setLoadingMatches(prev => ({ ...prev, [match.id]: true }));
        }

        const currentSets = getNormalizedSets(match?.sets);
        const localSets = scores[match.id]?.sets ? getNormalizedSets(scores[match.id].sets) : currentSets;

        let isSetUpdate = false;
        const mainFields = {};
        const setFields = {};

        const setFieldNames = ['game_score_1', 'game_score_2', 'tiebreak_score_1', 'tiebreak_score_2', 'point_score_1', 'point_score_2', 'serving_player'];

        Object.keys(newFields).forEach(k => {
            if (setFieldNames.includes(k)) {
                setFields[k] = newFields[k];
                isSetUpdate = true;
            } else {
                mainFields[k] = newFields[k];
            }
        });

        const newSets = [...localSets];
        // Ensure no empty slots
        for (let i = 0; i <= setIndex; i++) {
            if (!newSets[i]) newSets[i] = { number: i + 1 };
        }

        if (isSetUpdate) {
            newSets[setIndex] = { ...newSets[setIndex], ...setFields, number: setIndex + 1 };
        }

        // Мгновенный UI для админа
        let updated;
        if (isSetUpdate) {
           updated = { ...scores[match.id], ...newFields, sets: newSets };
        } else {
            updated = { ...scores[match.id], ...mainFields };
        }
        setScores(prev => ({ ...prev, [match.id]: updated }));
        
        if (setGroups) {
            setGroups(prev => prev.map(g => ({
                ...g,
                matches: g.matches.map(m => m.id === match.id ? { ...m, ...updated } : m)
            })));
        }

        let payloadToSync;
        if (isSetUpdate) {
            payloadToSync = { sets: [{ ...setFields, number: setIndex + 1 }] };
        } else {
            payloadToSync = { ...newFields };
        }
        // Отправка в синхронную очередь
        syncMatchApi(match.id, payloadToSync);
    };

    return (
        <BottomSheetPanel isOpen={isOpen} onClose={onClose} expandSignal={expandSignal} scrollTargetId={selectedMatchId != null ? `match-${selectedMatchId}` : null} title={t("Matches")} icon="list">
            <form className="modal-form">
                <div className="team-table matches">
                    {groups.map(group => {
                        const matches = buildMatches(group);
                        const groupCharCode = String.fromCharCode(64 + group.number);

                        return (
                            <React.Fragment key={group.id}>
                                <label style={{marginTop: 0}}>{t("Group")} {groupCharCode} ({group.number})</label>
                                {matches.map((m) => {
                                    const localKey = `${group.id}-${m.match_order}`;
                                    const match = m.match;
                                    const matchKey = match?.id ?? localKey;
                                    const canEdit = is_authorized && m.p1 && m.p2;
                                    const matched = pairMatchesSearch(m.p1, m.p2, group, tournamentGrade, searchChips, teamRoster, playerNames);

                                    const matchSets = scores[match?.id]?.sets ? getNormalizedSets(scores[match?.id]?.sets) : getNormalizedSets(match?.sets);
                                    const optimisticVals = match ? { ...matchSets[0], winner_side: match.winner_side, ...scores[match.id] } : {};
                                    const rows = [
                                        { side: 1, participant: m.p1, index: m.p1_index, games: optimisticVals.game_score_1, tb: optimisticVals.tiebreak_score_1 },
                                        { side: 2, participant: m.p2, index: m.p2_index, games: optimisticVals.game_score_2, tb: optimisticVals.tiebreak_score_2 }
                                    ];
                                    const activeSetIndex = activeSetIndexes[matchKey] || 0;
                                    const setActiveSetIndex = (updater) => setActiveSetIndexes(prev => ({
                                        ...prev,
                                        [matchKey]: typeof updater === "function" ? updater(prev[matchKey] || 0) : updater
                                    }));

                                    return (
                                        <div
                                            id={`match-${matchKey}`}
                                            key={matchKey}
                                            onClick={() => setSelectedMatchId(matchKey)}
                                            className={`team-card ${String(matchKey) === String(selectedMatchId) ? "selected" : (canEdit ? "editable" : "")} ${searchStateClass(matched)}`}
                                            style={{ marginBottom: "0" }}
                                        >
                                            <div className="team-name-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                                    {t("Group")} {groupCharCode}
                                                </span>
                                                {is_authorized && <SetSwitcher activeSetIndex={activeSetIndex} setActiveSetIndex={setActiveSetIndex} />}
                                            </div>
                                            <div>
                                                {rows.map((row, rowIndex) => (
                                                    <div key={rowIndex} className="team-player-row">
                                                        <MatchParticipantAvatars {...getAvatarData(row.participant, tournamentGrade, playerImages) || {}} grade={tournamentGrade} />
                                                        {getName(row.participant, tournamentGrade, row.index, groupCharCode, optimisticVals.winner_side, row.side, openTeamMenu, handlePlayerClick)}
                                                    </div>
                                                ))}
                                            </div>

                                            <MatchScoreColumns
                                                matchId={matchKey}
                                                winnerSide={optimisticVals.winner_side}
                                                rows={rows}
                                                canEdit={canEdit}
                                                is_authorized={is_authorized}
                                                matchSets={matchSets}
                                                activeSetIndex={activeSetIndex}
                                                onSave={(newFields, setIndex) => saveMatch(group.id, m, newFields, setIndex)}
                                            />

                                            <div className="team-name-row" style={{position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", marginBottom: "0"}}>
                                                {match?.start_time && <span>{match.start_time} · {t("Court")} {match.court}</span>}
                                                <MatchStatusSelect
                                                    match={match}
                                                    is_authorized={is_authorized}
                                                    onSave={(payload) => saveMatch(group.id, m, payload)}
                                                    min_height={true}
                                                    loader={loadingMatches[matchKey]}
                                                />
                                            </div>
                                            {/* Own row, not squeezed next to MatchStatusSelect above -
                                                see TournamentGridMatchesModal.js's identical comment:
                                                that select's own wrapper is `position: absolute; right: 0`
                                                (index.css:8042) and silently eats clicks meant for
                                                anything placed beside it in the same row. Only shown once
                                                the match row itself exists (saveMatch creates it lazily on
                                                first score/status save). */}
                                            {tournamentGrade === 3 && match && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                                                    <button
                                                        type="button"
                                                        className="action-button btn-green"
                                                        style={{ padding: "2px 10px", minHeight: "28px" }}
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDetailsMatch({ match, groupId: group.id, m }); }}
                                                    >
                                                        {t("Details")}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </form>
            <ParticipantActionsModal
                isOpen={!!teamMenu}
                onClose={() => setTeamMenu(null)}
                title={teamMenu?.title}
                actions={teamMenu?.actions}
            />
            <MatchDetailsModal
                isOpen={!!detailsMatch}
                onClose={() => setDetailsMatch(null)}
                match={detailsMatch?.match}
                tournamentGrade={tournamentGrade}
                tournamentId={tournamentId}
                is_authorized={is_authorized}
                isMultiSet={isMultiSet}
                onSave={(payload, setIndex) => saveMatch(detailsMatch.groupId, detailsMatch.m, payload, setIndex)}
            />
        </BottomSheetPanel>
    );
};

export default TournamentGroupsMatchesModal;
